import { useState } from 'react';
import { HAS_DEMO } from '@/lib/site-mode';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, GraduationCap, Sparkles, Loader2, X } from 'lucide-react';
import { MARK_ARC_FILLED_D, MARK_DOT, MARK_VIEWBOX, BRAND } from '@/lib/brand-assets';
import { saveOnboardingState, getOnboardingState, resetOnboarding } from '@/lib/onboarding-store';
import { supabase } from '@/lib/supabase';
import { isAdminSession } from '@/lib/user-role';
import { MicrosoftSignInButton } from '@/components/auth/MicrosoftSignInButton';
import {
  getRememberedAccounts, restoreRememberedAccount, forgetAccount,
  rememberAccount, accountFromSession, isQuickLoginEnabled, clearSessionParked,
  type RememberedAccount,
} from '@/lib/quick-login';

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';

export function SchoolLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Trusted-device quick login (admin-role remembered accounts)
  const [accounts, setAccounts] = useState<RememberedAccount[]>(() => getRememberedAccounts('school_admin'));
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [showAllForm, setShowAllForm] = useState(false);
  const hasChooser = accounts.length > 0 && !showAllForm;

  /** Establish an admin session — mirrors the password-login success path.
   *  Secondary contacts have their own Supabase user id but their school data
   *  lives under the primary admin's id (user_metadata.schoolId). */
  const finishAsSchool = async (session: Session): Promise<void> => {
    // Portal isolation: only an admin account may enter the admin portal. A
    // teacher (or any non-admin) authenticating here is signed straight back
    // out — the login page they used must never decide their privilege level.
    if (!isAdminSession(session)) {
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      sessionStorage.removeItem('cmc_school_token');
      setRestoringId(null);
      setIsLoading(false);
      setError('This is a teacher account. Please sign in from the teacher login page.');
      return;
    }
    clearSessionParked(); // active sign-in — the session is no longer just "parked" for the card
    sessionStorage.setItem('cmc_school_token', session.access_token);
    const state = getOnboardingState();
    const metaSchoolId = session.user.user_metadata?.schoolId as string | undefined;
    const effectiveSchoolId = (metaSchoolId && metaSchoolId !== session.user.id)
      ? metaSchoolId
      : session.user.id;
    saveOnboardingState({ ...state, isComplete: true, userType: 'school', supabaseUserId: effectiveSchoolId });
    navigate('/');
  };

  const pickAccount = async (acct: RememberedAccount) => {
    setRestoringId(acct.userId);
    setError('');
    const session = await restoreRememberedAccount(acct);
    if (!session) {
      setAccounts(getRememberedAccounts('school_admin'));
      setRestoringId(null);
      setShowAllForm(true);
      setError('That saved sign-in has expired. Please sign in again.');
      return;
    }
    void finishAsSchool(session);
  };

  const removeAccount = (userId: string) => {
    forgetAccount(userId);
    const next = getRememberedAccounts('school_admin');
    setAccounts(next);
    if (next.length === 0) setShowAllForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError || !signInData.session) {
      setError('Invalid email or password. Please try again.');
      setIsLoading(false);
      return;
    }

    if (rememberMe) {
      localStorage.setItem('cmc_remember_school', 'true');
    }
    const session = signInData.session;
    // Quick login: when the feature is on, remember this admin account so it
    // shows as a one-tap card next time. The opt-in itself is offered only at
    // logout (never here).
    if (session.user.user_metadata?.role === 'school_admin' && isQuickLoginEnabled()) {
      const acct = accountFromSession(session, 'password');
      if (acct) rememberAccount(acct);
    }
    void finishAsSchool(session);
  };

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 paper-dots-dark pointer-events-none" aria-hidden="true" />
      <div className="w-full max-w-md relative">
        <div className="bg-card rounded-lg shadow-2xl p-8">
          {hasChooser && (
            <>
              <div className="text-center mb-6">
                <svg viewBox={MARK_VIEWBOX} className="w-14 h-14 mx-auto mb-4" aria-hidden="true">
                  <path d={MARK_ARC_FILLED_D} fill={BRAND.navy700} />
                  <circle cx={MARK_DOT.cx} cy={MARK_DOT.cy} r={MARK_DOT.r} fill={BRAND.navy500} />
                </svg>
                <h1 className="font-display text-3xl font-bold mb-1">Welcome back</h1>
                <p className="text-muted-foreground text-sm">Tap your account to sign in</p>
              </div>
              <div className="space-y-2.5">
                {accounts.map((acct) => (
                  <div key={acct.userId} className="relative">
                    <button
                      type="button"
                      onClick={() => pickAccount(acct)}
                      disabled={restoringId !== null}
                      className="w-full flex items-center gap-3 p-3 pr-10 rounded-xl border border-border bg-background/60 hover:border-primary/50 hover:bg-primary/5 transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {initialsOf(acct.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-foreground truncate">{acct.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{acct.email}</div>
                      </div>
                      {restoringId === acct.userId && (
                        <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label={`Forget ${acct.name}`}
                      onClick={() => removeAccount(acct.userId)}
                      disabled={restoringId !== null}
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              {error && (
                <div className="mt-4 bg-destructive/10 text-destructive text-sm p-3 rounded-lg">{error}</div>
              )}
              <button
                type="button"
                onClick={() => { setShowAllForm(true); setError(''); }}
                className="w-full mt-4 py-2.5 text-sm text-primary hover:underline"
              >
                Use a different account
              </button>
            </>
          )}

          {!hasChooser && (
          <>
          {/* Header — the mark, no tile */}
          <div className="text-center mb-8">
            <svg viewBox={MARK_VIEWBOX} className="w-14 h-14 mx-auto mb-4" aria-hidden="true">
              <path d={MARK_ARC_FILLED_D} fill={BRAND.navy700} />
              <circle cx={MARK_DOT.cx} cy={MARK_DOT.cy} r={MARK_DOT.r} fill={BRAND.navy500} />
            </svg>
            <h1 className="font-display text-3xl font-bold mb-2">Welcome back</h1>
            <p className="text-muted-foreground">Sign in to your school account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="school-login-email" className="block text-sm font-semibold mb-2">Email Address</label>
              <Input
                id="school-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@school.edu"
                required
                autoFocus
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="school-login-password" className="block text-sm font-semibold mb-2">Password</label>
              <div className="relative">
                <Input
                  id="school-login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                />
                <label htmlFor="remember" className="text-sm cursor-pointer">
                  Remember me
                </label>
              </div>
              <button
                type="button"
                onClick={() => navigate('/school/forgot-password')}
                className="text-sm text-primary hover:underline px-1 py-2 -my-2"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Microsoft SSO — alternative sign-in for existing accounts */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>
          <MicrosoftSignInButton intent="school" />

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">New to CoverMyClass?</span>
            </div>
          </div>

          {/* Create Account — skips the welcome animation and lands on step 1 of the wizard */}
          <Button
            variant="secondary"
            className="w-full font-semibold"
            onClick={() => {
              resetOnboarding();
              saveOnboardingState({ userType: 'school', currentStep: 1 });
              navigate('/');
            }}
          >
            Create School Account
          </Button>

          {/* Teacher login — visually distinct button so teachers don't miss it */}
          <div className="mt-6 pt-5 border-t border-border">
            <p className="text-center text-xs uppercase tracking-wide text-muted-foreground mb-3 font-semibold">
              Teacher or Cover Staff?
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/teacher/login')}
              className="w-full border-2 border-primary/40 hover:border-primary hover:bg-primary/5 text-primary font-semibold flex items-center justify-center gap-2"
            >
              <GraduationCap size={18} />
              Sign in as Teacher
            </Button>
          </div>
          </>
          )}
        </div>

        {/* Subtle demo entry. Not in the app-only builds, which have no demo. */}
        {HAS_DEMO && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => navigate('/demo')}
            className="inline-flex items-center gap-1.5 px-2 py-2.5 text-xs text-white/70 hover:text-white underline-offset-4 hover:underline transition-colors"
          >
            <Sparkles className="w-3 h-3" />
            Try the demo. No signup required
          </button>
        </div>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-white/70 mt-6">
          © {new Date().getFullYear()} CoverMyClass. All rights reserved.
        </p>
      </div>
    </div>
  );
}
